'use client';

import type {ReactNode} from 'react';
import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {isCrawler} from 'Shared/isCrawler';

interface DialogRequest {
    id: string;
    priority: number;
}

interface DialogManagerContextValue {
    activeDialogId: string | null;
    release(id: string): void;
    requestOpen(id: string, priority: number): void;
}

const DialogManagerContext = createContext<DialogManagerContextValue | null>(null);

/**
 * Builds the shared dialog request queue and derives the currently active dialog id, bundled
 * with the `requestOpen`/`release` callbacks used to mutate it. Extracted into its own hook so
 * that `DialogManagerProvider`'s component body only makes a single hook call, as required by
 * the `@nfq/component-single-hook` lint rule.
 *
 * @returns The dialog manager context value to provide to descendants.
 */
const useDialogManagerValue = (): DialogManagerContextValue => {
    const [requests, setRequests] = useState<DialogRequest[]>([]);

    const requestOpen = useCallback((id: string, priority: number) => {
        setRequests(prev => (prev.some(request => request.id === id) ? prev : [...prev, {
            id,
            priority
        }]));
    }, []);

    const release = useCallback((id: string) => {
        setRequests(prev => prev.filter(request => request.id !== id));
    }, []);

    const activeDialogId = useMemo(() => {
        if (requests.length === 0) return null;

        return Array.from(requests).sort((a, b) => a.priority - b.priority)[0].id;
    }, [requests]);

    return useMemo(
        () => ({
            activeDialogId,
            release,
            requestOpen
        }),
        [activeDialogId, release, requestOpen]
    );
};

/**
 * Provides a shared queue for global overlay dialogs/banners (e.g. The language selection
 * popup, the app-update banner, the PWA install banner) so that only one of them is ever
 * visible at a time instead of stacking on top of each other. Dialogs register interest via
 * `useDialogSlot`; the request with the lowest `priority` number becomes active, and dismissing
 * it (which makes a dialog stop wanting to be open) automatically reveals the next one..
 *
 * @param props          The component props.
 * @param props.children The subtree that can consume `useDialogSlot`.
 * @returns A JSX element providing the dialog manager context to its children.
 *
 * @example
 * ```tsx
 * <DialogManagerProvider>
 *     <AppUpdateBanner />
 *     <LanguagePopup />
 *     <PwaInstallBanner />
 * </DialogManagerProvider>
 * ```
 */
const DialogManagerProvider = ({children}: {children: ReactNode}) => {
    const value = useDialogManagerValue();

    return (
        <DialogManagerContext.Provider value={value}>
            {children}
        </DialogManagerContext.Provider>
    );
};

DialogManagerProvider.displayName = 'DialogManagerProvider';

export {DialogManagerProvider};

/**
 * Registers a global dialog/banner with the surrounding `DialogManagerProvider` and reports
 * whether it currently holds the shared "slot", so competing overlays (language popup, app
 * update banner, PWA install banner, ...) render one at a time in priority order instead of
 * overlapping. A dialog that no longer wants to be open (e.g. Because the user dismissed it)
 * automatically frees the slot for the next-highest-priority dialog that still wants it..
 *
 * @param  id        A stable, unique identifier for the dialog, e.g. `'languagePopup'`.
 * @param  priority  Lower numbers take precedence when multiple dialogs want to open at once.
 * @param  wantsOpen Whether the dialog's own conditions say it should be visible right now.
 * @returns `true` when this dialog currently holds the slot and is allowed to render.
 * @throws {Error} If used outside of a `DialogManagerProvider`.
 *
 * @example
 * ```tsx
 * const isActive = useDialogSlot('languagePopup', 0, wantsOpen);
 * const isOpen = wantsOpen && isActive;
 * ```
 */
export const useDialogSlot = (id: string, priority: number, wantsOpen: boolean): boolean => {
    const context = useContext(DialogManagerContext);

    if (!context) {
        throw new Error('useDialogSlot must be used within a DialogManagerProvider');
    }

    const {activeDialogId, release, requestOpen} = context;
    // Search engine crawlers and link-preview bots shouldn't ever see these overlays —
    // they can't dismiss them and their presence in rendered/snapshotted HTML is noise.
    const effectiveWantsOpen = wantsOpen && !isCrawler();

    useEffect(() => {
        if (effectiveWantsOpen) {
            requestOpen(id, priority);
        } else {
            release(id);
        }

        return () => release(id);
    }, [id, priority, effectiveWantsOpen, requestOpen, release]);

    return activeDialogId === id;
};